import { toast } from "@/components/ui/toast"
import { MESSAGES } from "@/components/ui/toast-messages"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { CHROME_FADE_END_PX } from "@/lib/fade"

const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs))
}

const capitalize = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error(error)
  }
};

/**
 * Where in the scrollport an element has to land to be READ.
 *
 * Past the whole fade, not merely past the bar. Clearing only the header's own height parks
 * the element at the foot of the ramp, where it is drawn at about a tenth of full strength —
 * scrolled to precisely the right place and all but invisible.
 *
 * These park elements by hand instead of using scrollIntoView, so the shell's `scroll-padding`
 * does nothing for them and the clearance has to be repeated here.
 */
const REST_BELOW_HEADER = CHROME_FADE_END_PX + 16

/**
 * How far `target` sits from the top of its scrollport, in that scrollport's scroll
 * coordinates.
 *
 * Rects rather than `offsetTop`, which is measured from the nearest POSITIONED ancestor and so
 * answers a different question for every element depending on what happens to be above it.
 * The previous arithmetic here subtracted one element's offsetTop from another's without their
 * sharing an origin, and survived only because the two happened to cancel.
 */
const offsetWithin = (container: HTMLElement, target: HTMLElement) =>
  target.getBoundingClientRect().top -
  container.getBoundingClientRect().top +
  container.scrollTop

const scroll = (id: string) => {
  const container = document.getElementById("main-content-area");
  const target = document.getElementById(id);
  if (container && target) {
    container.scrollTo({
      top: offsetWithin(container, target) - REST_BELOW_HEADER,
      behavior: "smooth",
    });
  }
}

const jump = (id: string) => {
  const container = document.getElementById("main-content-area");
  const target = document.getElementById(id);
  if (container && target) {
    // Further down the page than `scroll` parks things: this is a jump to a paragraph the
    // reader is being SHOWN, and it wants some of what precedes it in view.
    container.scrollTo({
      top: offsetWithin(container, target) - REST_BELOW_HEADER - 64,
      behavior: "auto",
    });
  }
  const side = document.getElementById("sidebar-scrollable-container");
  const sideTarget = document.getElementById("sidebar_" + id);
  if (side && sideTarget) {
    // Its own scrollport, with no chrome over it.
    side.scrollTo({ top: offsetWithin(side, sideTarget) - 32, behavior: "auto" });
  }
}

const formatDate = (dateString: string) => {
  // 1. Guard against empty or null strings
  if (!dateString) return "N/A";

  const date = new Date(dateString);

  // 2. Check if the date object is valid
  // isNaN(date.getTime()) returns true if the date is "Invalid Date"
  if (isNaN(date.getTime())) {
    console.error(`Invalid date provided: ${dateString}`);
    return "Invalid Date";
  }

  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC", // Forces consistency between server and client
  }).format(date);
};

async function copyToClipboard(text: string): Promise<void> {

  if (!navigator.clipboard) {
    // Fallback for older browsers or non-secure contexts
    return fallbackCopyToClipboard(text);
  }

  try {
    toast(MESSAGES.clipboard.copied)
    await navigator.clipboard.writeText(text);
  } catch (err) {
    toast(MESSAGES.clipboard.failed)
    throw err;
  }
}

function fallbackCopyToClipboard(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  
  // Ensure the textarea is not visible but stays in the DOM
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  textArea.style.top = "0";
  document.body.appendChild(textArea);
  
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (!successful) throw new Error('Fallback copy failed');
  } catch (err) {
    console.error('Fallback: Unable to copy', err);
  }

  document.body.removeChild(textArea);
}

function cosineSimilarity(vecA: number[], vecB: number[]) {
  let dotProduct = 0;
  let mA = 0;
  let mB = 0;

  for (let i = 0; i < vecA.length; i++) {
    dotProduct += vecA[i] * vecB[i];
    mA += vecA[i] * vecA[i];
    mB += vecB[i] * vecB[i];
  }
  
  const similarity = dotProduct / (Math.sqrt(mA) * Math.sqrt(mB));
  return similarity || 0; // Handle division by zero
}


const downloadFile = (content: string, fileName: string, contentType: string) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export {
  cn,
  capitalize,
  copy,
  scroll,
  jump,
  formatDate,
  copyToClipboard,
  cosineSimilarity,
  downloadFile
}
